import { configureStore, createSlice } from '@reduxjs/toolkit';
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import {
    StateInterface
} from '../interfaces/interfaces';
import {
    getFilterPlaceholderText,
    getHideMyProducts,
    getProductsData, getShowAllProductsToCartBtnLink,
    ITEMS_PER_PAGE_1,
    refresh
} from '../services/list-page-provider';
import { saveToStorage } from '../services/list-page-storage-provider';
import { generateDeepLink, updateDeepLink } from '../services/deep-link-provider';
import { getIsAuthenticated } from '../../../domain/hidden-data-provider';
import { sendGAEvent } from '../../../domain/marketing/gtm-provider';
import { CompareTypeEnum } from '../compare/compare-type-enum';
import { getJSONFromHtml } from '../../../domain/common';
import { getABTest, sendABTestAnalytics } from '../../../domain/abtest/abtest-provider';

// https://dev13.waves.com/my-recommended-products
const isRecommended = document.querySelector('meta[name="is-recommended"]') != null;

const productsInitial = getProductsData();

// https://wavesaudio.atlassian.net/browse/DSWEB-1934
if(isRecommended && getIsAuthenticated() && productsInitial.length > 0 && !!productsInitial[0].resultSetType) {
    sendGAEvent('My Recommended Products Page', productsInitial[0].resultSetType);
}

// https://wavesaudio.atlassian.net/browse/DSWEB-3160
// https://wavesaudio.atlassian.net/browse/DSWEB-3285
const isBundles = window.location.pathname.startsWith('/bundles');
const isSoundGridServers = window.location.pathname.startsWith('/hardware/soundgrid-servers');
const isCompareVisible = isBundles || isSoundGridServers;
const compareType = isBundles ? CompareTypeEnum.Bundles : CompareTypeEnum.SoundGridServers;
const compareServersDocIdList = getJSONFromHtml(document.getElementById('compare-server-doc-id-list')) || [];

// https://wavesaudio.atlassian.net/browse/DSWEB-5397
const getSuperSaleAbTestValue = () : string => {
    const $listPageMeta = document.querySelector('meta[property="google-ecomm-pagetype"][content="category"]');
    if(!$listPageMeta) return '';

    const hasDiscountCondition = productsInitial.find(product => product.discount >= 75);
    if(!hasDiscountCondition) return '';

    const abtest = getABTest('ListSuperSaleBadge');
    if(!abtest?.isActive) return '';

    sendABTestAnalytics(abtest);
    return abtest.selected;
};

/**
 * initial state ----------
 */
const initialState: StateInterface = {
    productsInitial,
    products: [],
    loading: false,

    // filter
    filterTerm: '',
    filterProperty: 'documentName',
    filterPlaceholderText: getFilterPlaceholderText(),

    // pagination
    pagination: null,
    currentPage: 0,
    itemsPerPage: ITEMS_PER_PAGE_1,
    paginationRange: 10,

    // sort
    sortType: 'number', // text|number|datetime
    sortProperty: 'mainOrder',
    sortOrder: 'asc', // asc or desc
    showPriceSorting: true, // !!document.getElementById('sortbyprice'),

    // theme (layout)
    layoutMode: 'grid', // grid|list

    // counters
    localTime: (new Date()).getTime(),

    purchased: null,
    hideMyProducts: getHideMyProducts(),
    showLoginPage: false,

    // https://dev13.waves.com/my-recommended-products
    isRecommended,

    // https://wavesaudio.atlassian.net/browse/DSWEB-3146
    allProductsToCartBtnLink: getShowAllProductsToCartBtnLink(productsInitial),

    // https://wavesaudio.atlassian.net/browse/DSWEB-3160
    // https://wavesaudio.atlassian.net/browse/DSWEB-3285
    isCompareVisible,
    isComparePopupVisible: false,
    compareType,
    compareServersDocIdList,

    // https://wavesaudio.atlassian.net/browse/MRKT-25058
    // old:34, yellow-sbs:33, perpetual-link:33
    buttonsABCDAbTest: 'old',

    // https://wavesaudio.atlassian.net/browse/DSWEB-5397
    superSaleAbTest: getSuperSaleAbTestValue(),
};

// the main slice
const rootSlice = createSlice({
    name: 'root',
    initialState,
    reducers: {
        main(state, action) {
            return {
                ...state,
                ...action.payload,
            }
        },

        refresh(state, action) {

            const updatedState =  {
                ...state,
                ...action.payload,
            };

            const res = refresh(updatedState);
            updatedState.products = res.products;
            updatedState.pagination = res.pagination;

            return updatedState;
        },

        refreshAndStorage(state, action) {

            const updatedState =  {
                ...state,
                ...action.payload,
            };

            const res = refresh(updatedState);
            updatedState.products = res.products;
            updatedState.pagination = res.pagination;

            // update storage
            saveToStorage(updatedState);

            return updatedState;
        },

        refreshAndStorageDeepLink(state, action) {

            const updatedState =  {
                ...state,
                ...action.payload,
            };

            const res = refresh(updatedState);
            updatedState.products = res.products;
            updatedState.pagination = res.pagination;

            const deepLink = generateDeepLink(
                updatedState.sortProperty,
                updatedState.sortType,
                updatedState.sortOrder,
                updatedState.currentPage,
                updatedState.itemsPerPage,
                updatedState.layoutMode,
                updatedState.filterTerm
            );

            updateDeepLink(deepLink);
            saveToStorage(updatedState);

            return updatedState;
        },
    }
});

const store = configureStore({
    reducer: rootSlice.reducer,
})

export const rootActions = rootSlice.actions;
export default store;

/**
 * Infer the `RootState` and `AppDispatch` types from the store itself
 * https://redux.js.org/recipes/usage-with-typescript
 */
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

// Use throughout your app instead of plain `useDispatch` and `useSelector`
// https://redux.js.org/recipes/usage-with-typescript
export const useAppDispatch = () => useDispatch<AppDispatch>()
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector